// perlu.repo — ambil tiket in-scope (RLS) + waktu-masuk-status (SLA) + ronde (piutang).
// Dipakai perluBuild (menu Perlu Diperhatikan + Dashboard) DAN runReminders.
import { supabase } from '@core/supabase';
import { fetchAllChunked, fetchAllPaged } from '@core/paging';
import { MONEY_COLS, rowToTicket, fetchRounds, type TicketRow } from '@core/ticketMap';
import { financePendingStartMs, type LogEntry } from '@domain/sla';
import type { Ticket } from '@core/types';

export interface PerluTicket {
  no: string; status: string; unit: string; custNama: string; custWa: string | null; teknisi: string | null;
  prioritas: string; tglISO: string | null; estBiaya: number; jatuhTempo: string | null;
  hasil: string | null; surveyDone: boolean; estSelesai: string | null; enterMs: number;
  // Waktu-mulai fase MENUNGGU finance (badge/eskalasi SLA). NaN bila tak ada log terkait.
  verifyStartMs: number; refundStartMs: number;
  outlet: string; ticket: Ticket;
}

interface Row extends TicketRow {
  unit: string | null; prioritas: string | null; est_selesai: string | null;
  survey_done: boolean | null; customers: CustRel | CustRel[] | null;
}
interface CustRel { nama: string; wa: string | null }
const cn = (c: Row['customers']): string => (Array.isArray(c) ? c[0]?.nama : c?.nama) ?? '—';
const cw = (c: Row['customers']): string | null => (Array.isArray(c) ? c[0]?.wa : c?.wa) ?? null;

/** Semua tiket operasional (non-arsip, RLS-scoped) dengan data untuk perlu/reminder. */
export async function fetchPerluTickets(): Promise<PerluTicket[]> {
  const rows = await fetchAllPaged<Row>((dari, sampai) => supabase().from('tickets')
    .select(`${MONEY_COLS},unit,prioritas,est_selesai,survey_done,customers(nama,wa)`)
    .eq('archived', false).order('no').range(dari, sampai));
  const nos = rows.map((r) => r.no);
  const rounds = await fetchRounds(nos);

  // Waktu (ms) tiap tiket MASUK status berjalan = log terbaru dgn status===status tiket (basis SLA).
  // Sekaligus start-time fase menunggu finance (verify=markLunas, refund=submit) dari catatan.
  const enter = new Map<string, number>(), verifyStart = new Map<string, number>(), refundStart = new Map<string, number>();
  if (nos.length) {
    const lg = await fetchAllChunked<{ ticket_no: string; status: string; catatan: string | null; at: string }>(nos,
      (bagian, a, b) => supabase().from('ticket_logs').select('ticket_no,status,catatan,at')
        .in('ticket_no', bagian).order('ticket_no').order('at').range(a, b));
    const byNo = new Map<string, LogEntry[]>();
    for (const l of lg) {
      const a = byNo.get(l.ticket_no) ?? []; a.push({ status: l.status, at: l.at, catatan: l.catatan }); byNo.set(l.ticket_no, a);
    }
    for (const r of rows) {
      const ls = byNo.get(r.no) ?? [];
      let ms = NaN;
      for (const l of ls) if (l.status === r.status) ms = new Date(l.at).getTime();
      enter.set(r.no, ms);
      verifyStart.set(r.no, financePendingStartMs(ls, 'verify'));
      refundStart.set(r.no, financePendingStartMs(ls, 'refund'));
    }
  }

  return rows.map((r) => ({
    no: r.no, status: r.status, unit: r.unit ?? '—', custNama: cn(r.customers), custWa: cw(r.customers),
    teknisi: r.teknisi ?? null, prioritas: r.prioritas ?? 'P2', tglISO: r.tgl_iso ?? null,
    estBiaya: r.est_biaya ?? 0, jatuhTempo: r.jatuh_tempo ?? null, hasil: r.hasil ?? null,
    surveyDone: r.survey_done ?? false, estSelesai: r.est_selesai ?? null,
    enterMs: enter.get(r.no) ?? NaN, verifyStartMs: verifyStart.get(r.no) ?? NaN, refundStartMs: refundStart.get(r.no) ?? NaN,
    outlet: r.outlet, ticket: rowToTicket(r, rounds.get(r.no) ?? []),
  }));
}
